import type { FC } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import { TPO_KEEP_EVENT_MODAL } from '../../../config/testIds';
import type { CalendarRow } from '../../../types/calendar';
import useKeepEventMutation from '../hooks/mutations/useKeepEventMutation';

const antIcon = <LoadingOutlined style={{ fontSize: 16 }} spin />;

const CONFIRM_BUTTON_PROPS = { type: 'primary', style: { borderRadius: 6 } } as const;

const MODAL_WIDTH = 520;

export interface KeepEventModalProps {
    /** The row the action was triggered from; `null` keeps the modal closed. */
    record: CalendarRow | null;
    onClose: () => void;
}

const KeepEventModal: FC<KeepEventModalProps> = ({ record, onClose }) => {
    const { labels } = useTpoGenConfig();
    const keepFlagLabels = labels.keepFlags;
    const { mutate: setKeepEvent, isPending } = useKeepEventMutation({ onSuccess: onClose });

    if (!record) return null;

    const isSet = !!record.keep_event;

    return (
        <ConfirmationModal
            data-testid={TPO_KEEP_EVENT_MODAL}
            open
            onCancel={onClose}
            onSubmitClick={() => setKeepEvent({ eventId: record.event_id, keepEvent: !isSet })}
            okText={isPending ? <Spin indicator={antIcon} /> : keepFlagLabels.confirmYes}
            cancelText={labels.common.cancel}
            okButtonProps={CONFIRM_BUTTON_PROPS}
            loading={isPending}
            title={isSet ? keepFlagLabels.keepEventRemoveTitle : keepFlagLabels.keepEventTitle}
            description={
                <div className="bg-[#fafafa] rounded-lg p-2">
                    <p className="text-sm text-[#18181b]">
                        {isSet ? keepFlagLabels.keepEventRemoveNote : keepFlagLabels.keepEventNote}
                    </p>
                </div>
            }
            width={MODAL_WIDTH}
        />
    );
};

KeepEventModal.displayName = 'KeepEventModal';
export default KeepEventModal;
